"use client";

import { useState, type ReactNode } from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { CircleAlert } from "lucide-react";
import { useTranslations } from "next-intl";

import {
  DialogCancelButton,
  DialogDeleteButton,
} from "@/components/admin/settings-form-controls";
import { cn } from "@/lib/utils";

export type AdminDeleteConfirmAnchor =
  | Element
  | null
  | (() => Element | null)
  | React.RefObject<Element | null>

  | "pointer";

type PointerPoint = {
  x: number;
  y: number;
  at: number;

  el: Element | null;
};

let lastPointer: PointerPoint | null = null;

if (typeof document !== "undefined") {

  document.addEventListener(
    "pointerdown",
    (event) => {
      lastPointer = {
        x: event.clientX,
        y: event.clientY,
        at: Date.now(),
        el: event.target instanceof Element ? event.target : null,
      };
    },
    true,
  );
}

const POINTER_FRESH_MS = 1500;

export function isPointerAnchorFresh() {
  return Boolean(lastPointer && Date.now() - lastPointer.at < POINTER_FRESH_MS);
}

function createPointerAnchor(point: PointerPoint | null) {
  const rect = new DOMRect(point?.x ?? 0, point?.y ?? 0, 0, 0);
  const el = point?.el;

  return {
    getBoundingClientRect: () => rect,

    contextElement: el?.isConnected
      ? el
      : typeof document !== "undefined"
        ? document.body
        : undefined,
  };
}

export type AdminDeleteConfirmPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchor: AdminDeleteConfirmAnchor;

  align?: "start" | "center" | "end";

  title?: ReactNode;

  content?: ReactNode;

  showSaveHint?: boolean;
  saving?: boolean;
  disabled?: boolean;
  cancelDisabled?: boolean;
  confirmLabel?: string;
  deletingLabel?: string;
  onCancel?: () => void;
  onConfirm: () => void;
};

export function AdminDeleteConfirmPopover({
  open,
  onOpenChange,
  anchor,
  align = "center",
  title,
  content,
  showSaveHint = false,
  saving = false,
  disabled = false,
  cancelDisabled,
  confirmLabel,
  deletingLabel,
  onCancel,
  onConfirm,
}: AdminDeleteConfirmPopoverProps) {
  const t = useTranslations("site.ui");

  const [prevOpen, setPrevOpen] = useState(open);

  const [pointerAnchor, setPointerAnchor] = useState(() =>
    createPointerAnchor(open ? lastPointer : null),
  );

  if (open !== prevOpen) {
    setPrevOpen(open);

    if (open) {

      setPointerAnchor(createPointerAnchor(lastPointer));
    }
  }

  const resolvedAnchor = anchor === "pointer" ? pointerAnchor : anchor;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          anchor={resolvedAnchor}
          side="top"
          align={align}
          sideOffset={8}
          className="isolate z-50 data-[anchor-hidden]:hidden"
        >
          <PopoverPrimitive.Popup
            data-slot="admin-delete-confirm-popover"

            className={cn(
              "w-[260px] max-w-[calc(100svw-24px)] origin-(--transform-origin) rounded-lg border bg-popover p-3 text-popover-foreground shadow-[0_0_12px_0_rgb(0_0_0/0.1)] outline-none",
              "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
              "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            )}
          >
            <div className="flex gap-2">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-snug font-medium whitespace-normal [overflow-wrap:anywhere]">
                  {title ?? t("confirmPopoverTitle")}
                </p>
                <p className="mt-1 text-xs leading-relaxed whitespace-normal text-muted-foreground [overflow-wrap:anywhere]">
                  {content ?? t("deleteConfirmQuestion")}
                  {showSaveHint ? <span className="ml-1">{t("changesApplyAfterSave")}</span> : null}
                </p>
                {/* 与弹窗底部同一套按钮，气泡里压到 28px 高、12px 字，跟正文一个量级 */}
                <div className="mt-3 flex justify-end gap-2">
                  <DialogCancelButton
                    className="h-7 px-3 text-xs"
                    disabled={cancelDisabled ?? disabled}
                    onClick={() => {
                      onCancel?.();
                      onOpenChange(false);
                    }}
                  />
                  <DialogDeleteButton
                    className="h-7 px-3 text-xs"
                    saving={saving}
                    disabled={disabled}
                    confirmLabel={confirmLabel}
                    deletingLabel={deletingLabel}
                    onClick={onConfirm}
                  />
                </div>
              </div>
            </div>

            <PopoverPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-1px)] rotate-45 rounded-[2px] border-r border-b bg-popover data-[side=bottom]:top-1 data-[side=top]:-bottom-2.5" />
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
